import { BOOLEAN_OPERATORS, BOOLEAN_SEARCH_PARTS, CASTING_TOOL_SLUGS, DATE_FORMATS } from 'constants/index';

import { getBooleanExpressionString } from 'utils/casting';
import { createDate } from 'utils/date';
import { getProductionCredits } from 'utils/productions';

import { ENTITY_TYPES } from 'operabase-router/constants';

const extractEntityIds = (profiles = []) =>
  profiles
    .map(profile => profile?.contributorIdPrefix)
    .filter(Boolean)
    .join(',');

export const getDirectorConductorIds = ({ production, entityId }) => {
  const credits = getProductionCredits(production, {
    entityId,
    entityType: ENTITY_TYPES.ORGANIZATION,
  });

  const { conductors = {}, directors = {} } = credits;

  return {
    selectedConductors: extractEntityIds(conductors?.data),
    selectedDirectors: extractEntityIds(directors?.data),
  };
};

export const getWorkAndWorkRoleExpression = ({ workId, selectedRole, works }) => {
  if (!selectedRole?.id || !selectedRole?.type) {
    return null;
  }

  const queries = [];

  const addQuery = workIdToUse => {
    if (queries.length > 0) {
      queries.push({
        type: BOOLEAN_SEARCH_PARTS.OPERATOR,
        value: BOOLEAN_OPERATORS.AND,
      });
    }

    queries.push({
      type: BOOLEAN_SEARCH_PARTS.QUERY,
      value: {
        [CASTING_TOOL_SLUGS.WORK]: workIdToUse,
        [CASTING_TOOL_SLUGS.WORK_ROLE]: {
          id: selectedRole.id,
          type: selectedRole.type,
        },
      },
    });
  };

  let workIds = [];

  if (selectedRole?.selectedWorks?.length > 0) {
    workIds = selectedRole.selectedWorks.map(w => w.id);
  } else if (workId) {
    workIds = [workId];
  } else if (works?.length) {
    workIds = works.map(w => w.id);
  }

  [...new Set(workIds)].forEach(addQuery);

  if (queries.length) {
    return getBooleanExpressionString(queries);
  }

  return null;
};

const formatDatesWithGrouping = dates => {
  if (!dates?.length) {
    return '';
  }

  const grouped = dates
    .map(date => createDate(date))
    .sort((a, b) => a.valueOf() - b.valueOf())
    .reduce((acc, date) => {
      const year = date.year();
      const month = date.format('MMM');
      const key = `${year}-${month}`;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(date.format('DD'));
      return acc;
    }, {});

  let lastYear = null;
  let isFirst = true;

  return Object.entries(grouped)
    .map(([key, days]) => {
      const [year, month] = key.split('-');
      const prefix = year !== lastYear ? `${year} ` : '';
      lastYear = year;

      const formattedDays = days.map((d, i) => (isFirst && i === 0 ? `${d}m` : d)).join(', ');
      isFirst = false;

      return `${prefix}${month} ${formattedDays}`;
    })
    .join(', ');
};

export const formatGroupedVenueDates = (venueDates, venueList) => {
  if (!venueDates?.length) {
    return [];
  }

  const groupedByVenue = venueDates.reduce((acc, { venue, date }) => {
    const venueId = venue.id.toString();

    if (!acc[venueId]) {
      acc[venueId] = [];
    }

    acc[venueId].push(date);
    return acc;
  }, {});

  return Object.entries(groupedByVenue).map(([venueId, dateList]) => {
    const venue = venueList.find(v => v.id.toString() === venueId);
    const venueLabel = venue
      ? `${venue.name}, ${venue.city?.name || ''}, ${venue.country?.name || ''}`.replace(/,\s*,/g, ',').trim()
      : `Venue ID ${venueId}`;

    const formattedDates = formatDatesWithGrouping(dateList);

    return {
      venue: venueLabel,
      dates: formattedDates,
    };
  });
};

export const getDatesForJumpInReplacement = productionPerformancesDates => {
  const mappedNewDates = productionPerformancesDates.map(item => ({
    startDate: item.startDate,
    startTime: item.startTime,
    venue: item.venue || null,
  }));

  const mergedByDate = Object.values(
    [...mappedNewDates].reduce((acc, curr) => {
      const key = curr.startDate;

      if (!acc[key] || (curr.venue && !acc[key].venue)) {
        acc[key] = curr;
      }

      return acc;
    }, {}),
  );

  const today = createDate(new Date()).format(DATE_FORMATS.FULL_DATE);
  const futureDates = mergedByDate.filter(item => createDate(item.startDate).isSameOrAfter(today));

  const sortedDates = futureDates.sort((a, b) => createDate(a.startDate).unix() - createDate(b.startDate).unix());

  return sortedDates;
};
